"use client";

import { createContext, useContext, useRef, useMemo, PropsWithChildren } from "react";
import * as Sentry from "@sentry/nextjs";

type CorrelationContextType = {
  getLastCorrelationId: () => string | null;
  setLastCorrelationId: (id: string | null) => void;
};

const CorrelationContext = createContext<CorrelationContextType | null>(null);
const globalStore: { current: string | null } = { current: null };

const setGlobalCorrelationId = (id: string | null) => {
  globalStore.current = id;
  if (typeof window !== "undefined") {
    (window as Window & { __CEDE_LAST_CORRELATION_ID?: string | null }).__CEDE_LAST_CORRELATION_ID = id;
  }
  // Tag (not `extra`): tags are searchable in Sentry and survive a hook re-wrapping the error,
  // which is exactly when the id is needed to find the request's backend logs in CloudWatch.
  Sentry.setTag("correlationId", id ?? undefined);
};

const getGlobalCorrelationId = (): string | null => {
  if (typeof window !== "undefined") {
    const persisted = (window as Window & { __CEDE_LAST_CORRELATION_ID?: string | null }).__CEDE_LAST_CORRELATION_ID;
    if (persisted !== undefined) {
      globalStore.current = persisted;
    }
  }
  return globalStore.current;
};

/**
 * Stores the last correlationId generated by the API layer so error boundaries
 * can report it to Sentry and display it to users for support/debugging.
 */
export function CorrelationProvider({ children }: PropsWithChildren) {
  const lastCorrelationIdRef = useRef<string | null>(getGlobalCorrelationId());

  const contextValue = useMemo<CorrelationContextType>(
    () => ({
      getLastCorrelationId: () => lastCorrelationIdRef.current,
      setLastCorrelationId: (id: string | null) => {
        lastCorrelationIdRef.current = id;
        setGlobalCorrelationId(id);
      },
    }),
    [],
  );

  return <CorrelationContext.Provider value={contextValue}>{children}</CorrelationContext.Provider>;
}

export function useCorrelationId() {
  const context = useContext(CorrelationContext);
  if (!context) {
    throw new Error("useCorrelationId must be used within CorrelationProvider");
  }
  return context;
}

export const getLastCorrelationIdSnapshot = () => getGlobalCorrelationId();
